import { useState, useEffect, useCallback } from 'react'
import { formatCurrency } from '@/components/charts/chartUtils'
import { FREQUENCY_LABELS, FREQUENCY_SHORT_LABELS, DAY_OF_WEEK_LABELS, getMonthlyAmount, FREQUENCY_OPTIONS } from '@/lib/frequency'
import type { Frequency } from '@/types/database'

interface RecurringTemplate {
  id: number
  type: 'income' | 'expense'
  amount: number
  category_id: number
  category_name: string
  category_icon: string | null
  family_member_id: number
  family_member_name: string
  description: string | null
  day_of_month: number
  frequency: Frequency
  day_of_week: number | null
  anchor_date: string | null
  second_day_of_month: number | null
  is_active: number
}

interface Summary {
  totalTemplates: number
  activeTemplates: number
  monthlyIncome: number
  monthlyExpenses: number
  netMonthly: number
}

interface Category {
  id: number
  name: string
  icon: string | null
}

interface FamilyMember {
  id: number
  name: string
}

export function Recurring() {
  const [templates, setTemplates] = useState<RecurringTemplate[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingTemplate, setEditingTemplate] =
    useState<RecurringTemplate | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([])
  const [generating, setGenerating] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    type: 'expense' as 'income' | 'expense',
    amount: '',
    category_id: '',
    family_member_id: '',
    description: '',
    frequency: 'monthly' as Frequency,
    day_of_month: '1',
    day_of_week: '1', // 1 = Monday
    anchor_date: '',
    second_day_of_month: '15'
  })

  const fetchData = useCallback(async () => {
    try {
      const [templatesRes, categoriesRes, membersRes] = await Promise.all([
        fetch('/api/recurring'),
        fetch('/api/categories'),
        fetch('/api/family-members')
      ])

      if (templatesRes.ok) {
        const data = (await templatesRes.json()) as {
          templates: RecurringTemplate[]
          summary: Summary
        }
        setTemplates(Array.isArray(data.templates) ? data.templates : [])
        setSummary(data.summary ?? null)
      }

      if (categoriesRes.ok) {
        const data = (await categoriesRes.json()) as { categories: Category[] }
        setCategories(Array.isArray(data.categories) ? data.categories : [])
      }

      if (membersRes.ok) {
        const data = (await membersRes.json()) as {
          familyMembers: FamilyMember[]
        }
        setFamilyMembers(Array.isArray(data.familyMembers) ? data.familyMembers : [])
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Build payload based on frequency
    const payload: Record<string, unknown> = {
      type: formData.type,
      amount: parseFloat(formData.amount),
      category_id: parseInt(formData.category_id),
      family_member_id: parseInt(formData.family_member_id),
      description: formData.description || null,
      frequency: formData.frequency,
      day_of_month: parseInt(formData.day_of_month)
    }

    // Add frequency-specific fields
    if (formData.frequency === 'weekly') {
      payload.day_of_week = parseInt(formData.day_of_week)
    }
    if (formData.frequency === 'bi-weekly') {
      payload.anchor_date = formData.anchor_date || new Date().toISOString().split('T')[0]
    }
    if (formData.frequency === 'semi-monthly') {
      payload.second_day_of_month = parseInt(formData.second_day_of_month)
    }

    try {
      const url = editingTemplate
        ? `/api/recurring/${editingTemplate.id}`
        : '/api/recurring'
      const method = editingTemplate ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        setShowForm(false)
        setEditingTemplate(null)
        resetForm()
        fetchData()
      }
    } catch (error) {
      console.error('Error saving template:', error)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this recurring item?')) return

    try {
      const response = await fetch(`/api/recurring/${id}`, { method: 'DELETE' })
      if (response.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Error deleting template:', error)
    }
  }

  const handleToggleActive = async (template: RecurringTemplate) => {
    try {
      const response = await fetch(`/api/recurring/${template.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !template.is_active })
      })
      if (response.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Error toggling template:', error)
    }
  }

  const handleGenerateTransactions = async () => {
    if (
      !confirm(
        'Generate transactions from all active recurring templates for this month?'
      )
    )
      return

    setGenerating(true)
    try {
      const now = new Date()
      const response = await fetch('/api/recurring/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          month: now.getMonth() + 1,
          year: now.getFullYear()
        })
      })

      if (response.ok) {
        const result = (await response.json()) as {
          created: number
          skipped: number
        }
        alert(
          `Created ${result.created} transactions. ${result.skipped} were skipped (already exist).`
        )
      }
    } catch (error) {
      console.error('Error generating transactions:', error)
    } finally {
      setGenerating(false)
    }
  }

  const handleEdit = (template: RecurringTemplate) => {
    setEditingTemplate(template)
    setFormData({
      type: template.type,
      amount: template.amount.toString(),
      category_id: template.category_id.toString(),
      family_member_id: template.family_member_id.toString(),
      description: template.description || '',
      frequency: template.frequency || 'monthly',
      day_of_month: template.day_of_month.toString(),
      day_of_week: (template.day_of_week ?? 1).toString(),
      anchor_date: template.anchor_date || '',
      second_day_of_month: (template.second_day_of_month ?? 15).toString()
    })
    setShowForm(true)
  }

  const resetForm = () => {
    setFormData({
      type: 'expense',
      amount: '',
      category_id: '',
      family_member_id: '',
      description: '',
      frequency: 'monthly',
      day_of_month: '1',
      day_of_week: '1',
      anchor_date: '',
      second_day_of_month: '15'
    })
  }

  const filteredCategories = categories.filter((c) =>
    formData.type === 'income'
      ? [
          'Salary/Wages',
          'Investment Returns',
          'Side Income',
          'Rental Income'
        ].includes(c.name)
      : ![
          'Salary/Wages',
          'Investment Returns',
          'Side Income',
          'Rental Income'
        ].includes(c.name)
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-forest-dark">
            Recurring Items
          </h1>
          <p className="text-sage mt-1">
            Manage your recurring income and expenses
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleGenerateTransactions}
            disabled={generating}
            className="px-4 py-2 bg-ocean text-white rounded-lg text-sm hover:bg-ocean/90 transition-colors disabled:opacity-50"
          >
            {generating ? 'Generating...' : 'Generate This Month'}
          </button>
          <button
            onClick={() => {
              setEditingTemplate(null)
              resetForm()
              setShowForm(true)
            }}
            className="px-4 py-2 bg-forest text-white rounded-lg text-sm hover:bg-forest-dark transition-colors"
          >
            + Add Recurring
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-4 border border-stone/10">
            <p className="text-sm text-stone">Monthly Income</p>
            <p className="text-xl font-bold text-success tabular-nums">
              {formatCurrency(summary.monthlyIncome)}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-stone/10">
            <p className="text-sm text-stone">Monthly Expenses</p>
            <p className="text-xl font-bold text-danger tabular-nums">
              {formatCurrency(summary.monthlyExpenses)}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-stone/10">
            <p className="text-sm text-stone">Net Monthly</p>
            <p
              className={`text-xl font-bold tabular-nums ${summary.netMonthly >= 0 ? 'text-success' : 'text-danger'}`}
            >
              {formatCurrency(summary.netMonthly)}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-stone/10">
            <p className="text-sm text-stone">Active Items</p>
            <p className="text-xl font-bold text-forest-dark">
              {summary.activeTemplates} / {summary.totalTemplates}
            </p>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div className="bg-white rounded-xl shadow-sm border border-stone/10">
        <div className="p-4 border-b border-stone/10">
          <h2 className="text-lg font-semibold text-charcoal">
            Recurring Templates
          </h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sage">Loading...</div>
        ) : templates.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sage">No recurring items set up</p>
            <p className="text-sm text-stone mt-2">
              Add recurring income or expenses to automate your budget tracking
            </p>
          </div>
        ) : (
          <div className="divide-y divide-stone/10">
            {templates.map((template) => (
              <div
                key={template.id}
                className={`p-4 flex items-center justify-between ${
                  !template.is_active ? 'opacity-50 bg-stone/5' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${
                      template.type === 'income' ? 'bg-success' : 'bg-danger'
                    }`}
                  >
                    {template.category_icon ||
                      (template.type === 'income' ? '+' : '-')}
                  </div>
                  <div>
                    <p className="font-medium text-charcoal">
                      {template.description || template.category_name}
                    </p>
                    <p className="text-sm text-stone">
                      {template.category_name} • {template.family_member_name} •
                      {FREQUENCY_SHORT_LABELS[template.frequency || 'monthly']}
                      {template.frequency === 'weekly' && template.day_of_week !== null && ` (${DAY_OF_WEEK_LABELS[template.day_of_week]})`}
                      {(template.frequency === 'monthly' || !template.frequency) && ` (Day ${template.day_of_month})`}
                      {template.frequency === 'semi-monthly' && ` (Days ${template.day_of_month} & ${template.second_day_of_month || 15})`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p
                      className={`text-lg font-semibold tabular-nums ${
                        template.type === 'income' ? 'text-success' : 'text-danger'
                      }`}
                    >
                      {template.type === 'income' ? '+' : '-'}
                      {formatCurrency(template.amount)}
                    </p>
                    {template.frequency && template.frequency !== 'monthly' && (
                      <p className="text-xs text-stone">
                        ~{formatCurrency(getMonthlyAmount(template.amount, template.frequency))}/mo
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(template)}
                      className={`px-3 py-1 text-xs rounded-full ${
                        template.is_active
                          ? 'bg-success/10 text-success'
                          : 'bg-stone/10 text-stone'
                      }`}
                    >
                      {template.is_active ? 'Active' : 'Paused'}
                    </button>
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 text-sage hover:text-forest-dark transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-2 text-stone hover:text-danger transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md m-4">
            <h2 className="text-lg font-semibold text-charcoal mb-4">
              {editingTemplate ? 'Edit Recurring Item' : 'Add Recurring Item'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">
                  Type
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        type: 'expense',
                        category_id: ''
                      })
                    }
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.type === 'expense'
                        ? 'bg-danger text-white'
                        : 'bg-stone/10 text-stone'
                    }`}
                  >
                    Expense
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        type: 'income',
                        category_id: ''
                      })
                    }
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.type === 'income'
                        ? 'bg-success text-white'
                        : 'bg-stone/10 text-stone'
                    }`}
                  >
                    Income
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">
                  Category
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) =>
                    setFormData({ ...formData, category_id: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                  required
                >
                  <option value="">Select category</option>
                  {filteredCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">
                  Family Member
                </label>
                <select
                  value={formData.family_member_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      family_member_id: e.target.value
                    })
                  }
                  className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                  required
                >
                  <option value="">Select member</option>
                  {familyMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                  placeholder="e.g., Netflix subscription"
                />
              </div>

              {/* Frequency Selector */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">
                  Frequency
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) =>
                    setFormData({ ...formData, frequency: e.target.value as Frequency })
                  }
                  className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                >
                  {FREQUENCY_OPTIONS.map((freq) => (
                    <option key={freq} value={freq}>
                      {FREQUENCY_LABELS[freq]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Weekly: Day of Week */}
              {formData.frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">
                    Day of Week
                  </label>
                  <select
                    value={formData.day_of_week}
                    onChange={(e) =>
                      setFormData({ ...formData, day_of_week: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                  >
                    {DAY_OF_WEEK_LABELS.map((label, index) => (
                      <option key={index} value={index}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Bi-weekly: Anchor Date */}
              {formData.frequency === 'bi-weekly' && (
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">
                    Start Date (first payment)
                  </label>
                  <input
                    type="date"
                    value={formData.anchor_date}
                    onChange={(e) =>
                      setFormData({ ...formData, anchor_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-stone mt-1">
                    Payments occur every 14 days from this date
                  </p>
                </div>
              )}

              {/* Semi-monthly: Two days of month */}
              {formData.frequency === 'semi-monthly' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">
                      First Day
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="28"
                      value={formData.day_of_month}
                      onChange={(e) =>
                        setFormData({ ...formData, day_of_month: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">
                      Second Day
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="28"
                      value={formData.second_day_of_month}
                      onChange={(e) =>
                        setFormData({ ...formData, second_day_of_month: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Monthly/Quarterly/Annual: Day of Month */}
              {(formData.frequency === 'monthly' || formData.frequency === 'quarterly' || formData.frequency === 'annual') && (
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">
                    Day of Month
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.day_of_month}
                    onChange={(e) =>
                      setFormData({ ...formData, day_of_month: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-stone/20 rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-stone mt-1">
                    {formData.frequency === 'quarterly' && 'Occurs in Jan, Apr, Jul, Oct'}
                    {formData.frequency === 'annual' && 'Occurs once per year in January'}
                    {formData.frequency === 'monthly' && 'For months with fewer days, it will use the last day'}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false)
                    setEditingTemplate(null)
                  }}
                  className="flex-1 py-2 border border-stone/20 rounded-lg text-charcoal hover:bg-stone/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-forest text-white rounded-lg hover:bg-forest-dark transition-colors"
                >
                  {editingTemplate ? 'Save Changes' : 'Add Recurring'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
